import useResetableStorage from "app/util/useResetableStorage";
import { createContext, useCallback, useContext, useReducer } from "react";

export interface GameState {
  id?: string;

  // Should only be true for players who have never visited the page before
  isNewPlayer?: boolean;

  gameTime: number;
  boardState: string; // Stringified JSON array
  isGameOver: boolean;

  gameStreakComplete?: boolean;

  // True if the daily game data has been started today (but not the practice game)
  isDailyLevelStarted: boolean;

  // These are changed via the settings menu
  showPlayTime?: boolean;
  isAudioMuted?: boolean;
}

export const FRESH_GAME_STATE: GameState = {
  gameTime: 0,
  boardState: "[]",
  isGameOver: false,
  isDailyLevelStarted: false,
};

export const NEW_PLAYER_STATE: GameState = {
  ...FRESH_GAME_STATE,
  isNewPlayer: true,
};

export const GameAction = {
  PRACTICE_ROUND_START: "PRACTICE_ROUND_START",
  DAILY_LEVEL_START: "DAILY_LEVEL_START",
  NEW_GAME_DATA_FOUND: "NEW_GAME",
  GAME_RESET: "GAME_RESET",
  GAME_OVER: "GAME_OVER",
  VIEW_COMPLETED_GRID: "VIEW_COMPLETED_GRID",
  GAME_TIME_INCREMENTED: "GAME_TIME_CHANGED",
  BOARD_STATE_UDPATED: "BOARD_STATE_UPDATED",
  GAME_STREAK_COMPLETED: "GAME_STREAK_COMPLETED",
  AUDIO_MUTE_CHANGED: "AUDIO_MUTE_CHANGED",
  SHOW_PLAY_TIME_CHANGED: "SHOW_PLAY_TIME_CHANGED",
} as const;

export type GameAction =
  | { type: typeof GameAction.PRACTICE_ROUND_START }
  | { type: typeof GameAction.GAME_RESET }
  | { type: typeof GameAction.NEW_GAME_DATA_FOUND; payload: { gameId: string } }
  | { type: typeof GameAction.DAILY_LEVEL_START }
  | { type: typeof GameAction.GAME_OVER }
  | { type: typeof GameAction.GAME_TIME_INCREMENTED }
  | {
      type: typeof GameAction.BOARD_STATE_UDPATED;
      payload: { newBoardState: string };
    }
  | { type: typeof GameAction.GAME_STREAK_COMPLETED }
  | {
      type: typeof GameAction.AUDIO_MUTE_CHANGED;
      payload: { isMuted: boolean };
    }
  | {
      type: typeof GameAction.SHOW_PLAY_TIME_CHANGED;
      payload: { showPlayTime: boolean };
    };

function gameReducer(state: GameState, action: GameAction) {
  switch (action.type) {
    case GameAction.PRACTICE_ROUND_START: {
      return {
        ...state,
        isNewPlayer: false,
      }
    }
    case GameAction.GAME_RESET: {
      return {
        ...NEW_PLAYER_STATE,
        isAudioMuted: false,
        showPlayTime: false,
      };
    }
    case GameAction.NEW_GAME_DATA_FOUND: {
      return {
        ...FRESH_GAME_STATE,
        isNewPlayer: state.isNewPlayer ?? true,
        showPlayTime: state.showPlayTime ?? false,
        isAudioMuted: state.isAudioMuted ?? false,
        id: action.payload.gameId,
      };
    }
    case GameAction.DAILY_LEVEL_START: {
      return {
        ...state,
        ...FRESH_GAME_STATE,
        isNewPlayer: false,
        isDailyLevelStarted: true,
      };
    }
    case GameAction.GAME_OVER: {
      return {
        ...state,
        isGameOver: true,
      };
    }
    case GameAction.GAME_TIME_INCREMENTED: {
      return {
        ...state,
        gameTime: state.gameTime + 1,
      };
    }
    case GameAction.BOARD_STATE_UDPATED: {
      return {
        ...state,
        boardState: action.payload.newBoardState,
      };
    }
    case GameAction.GAME_STREAK_COMPLETED: {
      return {
        ...state,
        gameStreakComplete: true,
      };
    }
    case GameAction.AUDIO_MUTE_CHANGED: {
      return {
        ...state,
        isAudioMuted: action.payload.isMuted,
      };
    }
    case GameAction.SHOW_PLAY_TIME_CHANGED: {
      return {
        ...state,
        showPlayTime: action.payload.showPlayTime,
      };
    }
    default:
      return state;
  }
}

export function useGameReducer(storageKey = "PIXELLENCE_GAME_STATE") {
  const serializer = (state: GameState): string =>
    JSON.stringify({
      ...state,
    });

  const deserializer = (value: string): GameState => {
    const parsed = JSON.parse(value);
    return {
      ...parsed,
    };
  };

  const [savedState, setSaveState] = useResetableStorage(
    storageKey,
    FRESH_GAME_STATE,
    {
      raw: false,
      serializer,
      deserializer,
    },
  );

  const reducerLocalStorage = useCallback(
    (state: GameState, action: GameAction) => {
      const newState = gameReducer(state, action);
      setSaveState(newState);
      return newState;
    },
    [setSaveState],
  );

  return useReducer(reducerLocalStorage, savedState || FRESH_GAME_STATE);
}

export const GameActionDispatchContext = createContext<
  React.Dispatch<GameAction>
>(() => {});

export function useGameActionDispatch() {
  return useContext(GameActionDispatchContext);
}

export const GameStateContext = createContext<GameState>(FRESH_GAME_STATE);

export default function useGameState() {
  return useContext(GameStateContext);
}
